/**
 * The bar across the content column: where you are, and what to do here.
 *
 * Sticky, because the breadcrumb and the screen's primary action are worth
 * keeping reachable down a long table, and because the drawer trigger has to
 * stay in reach on a narrow screen.
 *
 * @module components/ui/Topbar
 */

import React, { type ReactNode, useCallback } from "react";

import { IconButton } from "./Button";

export interface TopbarProps {
  /** The product mark and name, where there is no breadcrumb to show. */
  brand?: ReactNode;
  /** `false` where there is no navigation drawer for the trigger to open. */
  navigation?: boolean;
  /** Usually a Breadcrumb. */
  breadcrumb?: ReactNode;
  /** The screen's one primary action, if it has one. */
  action?: ReactNode;
  /** Notifications, theme, the user menu. */
  utilities?: ReactNode;
  /** The id of the drawer checkbox this opens. */
  drawerId?: string;
  "data-testid"?: string;
}

/**
 * @example
 * ```tsx
 * <Topbar breadcrumb={<Breadcrumb items={crumbs} />} action={<Button>New programme</Button>} />
 * ```
 */
/**
 * Focus a control that is still transitioning into view.
 *
 * daisyUI delays the drawer's visibility transition by 100ms, and `focus()` is
 * discarded inside a `visibility: hidden` subtree, so the first attempt after
 * opening is always lost. Retried until it takes rather than timed to that
 * delay, which is daisyUI's to change.
 *
 * `isOpen` is what makes a generous budget safe: a retry that fires after the
 * drawer has closed would otherwise pull focus off whatever the person moved
 * to and park it on a control they can no longer see.
 */
function focusWhenVisible(element: HTMLElement, isOpen: () => boolean, attempts = 20): void {
  if (!isOpen()) {
    return;
  }
  element.focus();
  if (document.activeElement === element || attempts === 0) {
    return;
  }
  window.setTimeout(() => {
    focusWhenVisible(element, isOpen, attempts - 1);
  }, 30);
}

export function Topbar({
  brand = undefined,
  navigation = true,
  breadcrumb = undefined,
  action = undefined,
  utilities = undefined,
  drawerId = "nav-drawer",
  "data-testid": testId,
}: TopbarProps): React.ReactElement {
  const openDrawer = useCallback(() => {
    const toggle = document.getElementById(drawerId);
    if (!(toggle instanceof HTMLInputElement)) {
      return;
    }
    toggle.checked = true;
    // Announces the change AppShell listens for: setting `.checked` in script
    // fires no event of its own, and the shell uses it to take the page behind
    // the overlay out of the focus order.
    toggle.dispatchEvent(new Event("change"));
    // Focus goes with it. `drawer-side` precedes `drawer-content`, so focus
    // left on this trigger sends the next Tab forward into the page behind the
    // overlay rather than into the navigation. Only the marked control: a
    // selector list matches in document order, and the aria-hidden overlay
    // comes first inside `.drawer-side`.
    // comment-length-ok: names both traps this one line has already fallen into
    const side = toggle.parentElement?.querySelector<HTMLElement>(
      ".drawer-side [data-drawer-close]",
    );
    if (side) {
      focusWhenVisible(side, () => toggle.checked);
    }
  }, [drawerId]);

  return (
    <header
      className="sticky top-0 z-[50] flex h-(--sq-topbar-height) items-center justify-between gap-4 border-b border-base-300 bg-base-100 px-8 max-lg:gap-2 max-lg:px-4"
      data-testid={testId}
    >
      <div className="flex min-w-0 items-center gap-3">
        {/* A button rather than daisyUI's label: the checkbox it would point at
            is display:none, and a label is not focusable, so the label pattern
            leaves the whole navigation unreachable from a keyboard. */}
        {navigation && (
          <IconButton
            icon="list"
            aria-label="Open navigation"
            size="sm"
            className="lg:hidden"
            onClick={openDrawer}
            // What `AppShell.close` hands focus back to.
            data-drawer-open=""
            data-testid="open-navigation"
          />
        )}
        {brand}
        {breadcrumb}
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {action}
        {utilities}
      </div>
    </header>
  );
}
